/**
 * PrivilegeEditorDialog — Formulaire creation/edition Escale Privilege.
 * Modale plein-ecran responsive avec tous les champs.
 */
import { useEffect, useMemo, useState } from "react";
import {
  getAdminPrivilege, createAdminPrivilege, updateAdminPrivilege, activateAdminPrivilege, getMedia,
} from "../lib/api";
import { getPublicThumbnail } from "../lib/mediaUrl";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import MediaSelector from "../components/MediaSelector";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Loader2, Eye, EyeOff, Image as ImageIcon, X, Sparkles } from "lucide-react";

const CATEGORIES = [
  { value: "croisieres", label: "Croisières" },
  { value: "hotels", label: "Hôtels" },
  { value: "sejours", label: "Séjours" },
  { value: "excursions", label: "Excursions" },
  { value: "accessoires", label: "Accessoires" },
  { value: "bagagerie", label: "Bagagerie" },
  { value: "autre", label: "Autre" },
];

const STATUSES = [
  { value: "draft", label: "Brouillon" },
  { value: "scheduled", label: "Programmée" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspendue" },
  { value: "ended", label: "Terminée" },
];

const ACCESS_TYPES = [
  { value: "code", label: "Code promo (à révéler à l'adhérent)" },
  { value: "external_link", label: "Lien partenaire (URL)" },
  { value: "reservation", label: "Instructions de réservation" },
  { value: "direct_payment", label: "Paiement direct (bientôt disponible)" },
];

const EMPTY_FORM = {
  title: "", hook: "", description: "", category: "croisieres",
  partner_id: null, main_image_id: null, gallery_media_ids: [],
  public_price: "", member_price: "",
  start_date: "", end_date: "",
  quota: "", status: "draft",
  conditions: "", destination_country: "",
  priority: 0, featured_on_homepage: false,
  access_type: "code",
  code_value: "", external_url: "",
  reservation_instructions: "", direct_payment_amount: "",
};

function isoToLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
  } catch { return ""; }
}
function localInputToIso(v) {
  if (!v) return null;
  return new Date(v).toISOString();
}
function numOrNull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function MediaThumb({ mediaId, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!mediaId) { setUrl(null); return; }
    let alive = true;
    getMedia(mediaId).then((r) => alive && setUrl(getPublicThumbnail(r.data, 300))).catch(() => alive && setUrl(null));
    return () => { alive = false; };
  }, [mediaId]);
  if (!url) return <div className={`bg-secondary flex items-center justify-center text-muted-foreground ${className}`}><ImageIcon className="h-6 w-6 opacity-40" /></div>;
  return <img src={url} alt="" className={className} />;
}

export default function PrivilegeEditorDialog({ open, onOpenChange, privilegeId, partners = [] }) {
  const isEdit = !!privilegeId;
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [mediaSelectorMain, setMediaSelectorMain] = useState(false);
  const [mediaSelectorGallery, setMediaSelectorGallery] = useState(false);
  const [existingCodePresent, setExistingCodePresent] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!isEdit) {
      setForm(EMPTY_FORM);
      setExistingCodePresent(false);
      return;
    }
    setLoading(true);
    getAdminPrivilege(privilegeId).then((res) => {
      const d = res.data;
      setForm({
        title: d.title || "", hook: d.hook || "", description: d.description || "",
        category: d.category || "croisieres",
        partner_id: d.partner_id || null,
        main_image_id: d.main_image_id || null,
        gallery_media_ids: d.gallery_media_ids || [],
        public_price: d.public_price ?? "",
        member_price: d.member_price ?? "",
        start_date: isoToLocalInput(d.start_date),
        end_date: isoToLocalInput(d.end_date),
        quota: d.quota ?? "",
        status: d.status || "draft",
        conditions: d.conditions || "",
        destination_country: d.destination_country || "",
        priority: d.priority ?? 0,
        featured_on_homepage: !!d.featured_on_homepage,
        access_type: d.access_type || "code",
        code_value: d.code_value || "",
        external_url: d.external_url || "",
        reservation_instructions: d.reservation_instructions || "",
        direct_payment_amount: d.direct_payment_amount ?? "",
      });
      setExistingCodePresent(!!d.code_present);
    }).catch(() => {
      toast.error("Erreur chargement offre");
      onOpenChange(false);
    }).finally(() => setLoading(false));
  }, [open, privilegeId, isEdit, onOpenChange]);

  const savings = useMemo(() => {
    const pub = numOrNull(form.public_price);
    const mem = numOrNull(form.member_price);
    if (pub == null || mem == null || pub <= 0 || mem > pub) return null;
    const amount = Math.round((pub - mem) * 100) / 100;
    const percent = Math.round((pub - mem) / pub * 100);
    return { amount, percent };
  }, [form.public_price, form.member_price]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const buildPayload = () => {
    const payload = {
      title: form.title.trim(),
      hook: form.hook.trim(),
      description: form.description,
      category: form.category,
      partner_id: form.partner_id || null,
      main_image_id: form.main_image_id || null,
      gallery_media_ids: form.gallery_media_ids || [],
      public_price: numOrNull(form.public_price),
      member_price: numOrNull(form.member_price),
      start_date: localInputToIso(form.start_date),
      end_date: localInputToIso(form.end_date),
      quota: numOrNull(form.quota),
      status: form.status,
      conditions: form.conditions || null,
      destination_country: form.destination_country || null,
      priority: Number(form.priority) || 0,
      featured_on_homepage: !!form.featured_on_homepage,
      access_type: form.access_type,
      external_url: form.access_type === "external_link" ? (form.external_url || null) : null,
      reservation_instructions: form.access_type === "reservation" ? (form.reservation_instructions || null) : null,
      direct_payment_amount: form.access_type === "direct_payment" ? numOrNull(form.direct_payment_amount) : null,
    };
    // code_value: n'envoyer que si modifie (creation ou edition avec valeur saisie)
    if (form.access_type === "code") {
      if (!isEdit || form.code_value !== "") {
        payload.code_value = form.code_value || null;
      }
    }
    return payload;
  };

  const validate = (payload) => {
    if (!payload.title) return "Titre requis";
    if (!payload.hook) return "Accroche requise";
    if (!payload.description) return "Description requise";
    if (payload.access_type === "external_link" && !payload.external_url) return "URL partenaire requise";
    if (payload.access_type === "reservation" && !payload.reservation_instructions) return "Instructions de réservation requises";
    if (payload.access_type === "code" && !isEdit && !payload.code_value) return "Code promo requis";
    return null;
  };

  const handleSave = async (activateAfter = false) => {
    const payload = buildPayload();
    const err = validate(payload);
    if (err) { toast.error(err); return; }
    if (activateAfter) payload.status = "active";
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        saved = await updateAdminPrivilege(privilegeId, payload);
      } else {
        saved = await createAdminPrivilege(payload);
      }
      if (activateAfter && saved.data.status !== "active") {
        try { await activateAdminPrivilege(saved.data.id); } catch (e) { /* silencieux */ }
      }
      toast.success(isEdit ? "Offre mise à jour" : (activateAfter ? "Offre créée et activée" : "Offre créée"));
      onOpenChange(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onOpenChange(false); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="privilege-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary">
            <Sparkles className="h-5 w-5" />
            {isEdit ? "Modifier l'offre" : "Nouvelle Escale Privilège"}
          </DialogTitle>
          <DialogDescription>
            Renseignez les informations de l'offre partenaire. Les prix, dates et code promo peuvent être ajustés à tout moment.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-muted-foreground">
            <Loader2 className="h-6 w-6 mx-auto animate-spin" />
          </div>
        ) : (
          <form
            autoComplete="off"
            onSubmit={(e) => e.preventDefault()}
            className="space-y-5 py-2"
          >
            {/* Honeypot anti-autofill : champs bidons captures par les password managers avant qu'ils n'atteignent les vrais champs */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
              <input type="text" name="username" autoComplete="username" tabIndex={-1} />
              <input type="password" name="password" autoComplete="current-password" tabIndex={-1} />
            </div>
            {/* Contenu editorial */}
            <div className="space-y-3">
              <div>
                <Label>Titre *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => update({ title: e.target.value })}
                  placeholder="Ex: Croisière Baltique Oceania -20%"
                  data-testid="privilege-form-title"
                />
              </div>
              <div>
                <Label>Accroche *</Label>
                <Input
                  value={form.hook}
                  onChange={(e) => update({ hook: e.target.value })}
                  placeholder="Une phrase percutante pour donner envie"
                  maxLength={200}
                  data-testid="privilege-form-hook"
                />
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => update({ description: e.target.value })}
                  placeholder="Description détaillée (markdown accepté)..."
                  rows={5}
                  data-testid="privilege-form-description"
                />
              </div>
            </div>

            {/* Categorie & Partenaire */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => update({ category: v })}>
                  <SelectTrigger data-testid="privilege-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Partenaire</Label>
                <Select
                  value={form.partner_id || "__none__"}
                  onValueChange={(v) => update({ partner_id: v === "__none__" ? null : v })}
                >
                  <SelectTrigger data-testid="privilege-form-partner"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Aucun —</SelectItem>
                    {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Medias */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Image principale</Label>
                <div className="flex items-center gap-3 mt-1">
                  {form.main_image_id && (
                    <MediaThumb mediaId={form.main_image_id} className="w-24 h-16 rounded object-cover flex-shrink-0" />
                  )}
                  <div className="flex flex-col gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setMediaSelectorMain(true)}
                      data-testid="privilege-form-main-image-btn"
                    >
                      <ImageIcon className="h-4 w-4 mr-2" />
                      {form.main_image_id ? "Changer" : "Choisir une image"}
                    </Button>
                    {form.main_image_id && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => update({ main_image_id: null })}>
                        <X className="h-4 w-4 mr-1" /> Retirer
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <Label>Galerie ({form.gallery_media_ids.length})</Label>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setMediaSelectorGallery(true)}
                    data-testid="privilege-form-gallery-btn"
                  >
                    <ImageIcon className="h-4 w-4 mr-2" /> Ajouter des médias
                  </Button>
                  {form.gallery_media_ids.length > 0 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => update({ gallery_media_ids: [] })}>
                      Vider
                    </Button>
                  )}
                </div>
                {form.gallery_media_ids.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {form.gallery_media_ids.map((mid) => (
                      <div key={mid} className="relative group">
                        <MediaThumb mediaId={mid} className="w-full aspect-square rounded object-cover" />
                        <button
                          type="button"
                          onClick={() => update({ gallery_media_ids: form.gallery_media_ids.filter((x) => x !== mid) })}
                          className="absolute top-1 right-1 bg-red-600/90 hover:bg-red-700 text-white rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Retirer"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Prix */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Prix public (€)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.public_price}
                  onChange={(e) => update({ public_price: e.target.value })}
                  placeholder="Optionnel"
                  data-testid="privilege-form-public-price"
                />
              </div>
              <div>
                <Label>Prix membre (€)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.member_price}
                  onChange={(e) => update({ member_price: e.target.value })}
                  placeholder="Optionnel"
                  data-testid="privilege-form-member-price"
                />
              </div>
              <div>
                <Label>Économie</Label>
                <div className="h-10 flex items-center px-3 rounded-md border border-input bg-secondary text-sm">
                  {savings ? (
                    <span className="text-green-700 font-medium">
                      {savings.amount.toFixed(2)} € (-{savings.percent}%)
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
              </div>
            </div>

            {/* Dates & Quota */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Début</Label>
                <Input
                  type="datetime-local"
                  lang="fr-FR"
                  value={form.start_date}
                  onChange={(e) => update({ start_date: e.target.value })}
                  data-testid="privilege-form-start-date"
                />
                {form.start_date && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {(() => { try { return format(new Date(form.start_date), "EEEE d MMMM yyyy 'à' HH'h'mm", { locale: fr }); } catch { return ""; } })()}
                  </p>
                )}
              </div>
              <div>
                <Label>Fin</Label>
                <Input
                  type="datetime-local"
                  lang="fr-FR"
                  value={form.end_date}
                  onChange={(e) => update({ end_date: e.target.value })}
                  data-testid="privilege-form-end-date"
                />
                {form.end_date && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {(() => { try { return format(new Date(form.end_date), "EEEE d MMMM yyyy 'à' HH'h'mm", { locale: fr }); } catch { return ""; } })()}
                  </p>
                )}
              </div>
              <div>
                <Label>Quota (illimité si vide)</Label>
                <Input
                  type="number" min="0"
                  value={form.quota}
                  onChange={(e) => update({ quota: e.target.value })}
                  placeholder="—"
                  data-testid="privilege-form-quota"
                />
              </div>
            </div>

            {/* Statut & Affichage */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Statut</Label>
                <Select value={form.status} onValueChange={(v) => update({ status: v })}>
                  <SelectTrigger data-testid="privilege-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priorité (tri décroissant)</Label>
                <Input
                  type="number"
                  value={form.priority}
                  onChange={(e) => update({ priority: e.target.value })}
                  data-testid="privilege-form-priority"
                />
              </div>
              <div>
                <Label>Destination</Label>
                <Input
                  value={form.destination_country}
                  onChange={(e) => update({ destination_country: e.target.value })}
                  placeholder="France, Italie..."
                  data-testid="privilege-form-destination"
                />
              </div>
            </div>

            <div className="flex items-center justify-between bg-secondary/50 rounded-md p-3">
              <div>
                <Label className="mb-0">Mettre en avant sur la page d'accueil</Label>
                <p className="text-xs text-muted-foreground">Popup + priorité maximale</p>
              </div>
              <Switch
                checked={form.featured_on_homepage}
                onCheckedChange={(v) => update({ featured_on_homepage: v })}
                data-testid="privilege-form-featured"
              />
            </div>

            {/* Conditions */}
            <div>
              <Label>Conditions</Label>
              <Textarea
                value={form.conditions}
                onChange={(e) => update({ conditions: e.target.value })}
                placeholder="Ex: Valable sur nouvelles réservations, non cumulable..."
                rows={3}
                data-testid="privilege-form-conditions"
              />
            </div>

            {/* Type d'acces */}
            <div className="border-t border-border pt-4 space-y-3">
              <Label>Type d'accès</Label>
              <Select value={form.access_type} onValueChange={(v) => update({ access_type: v })}>
                <SelectTrigger data-testid="privilege-form-access-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACCESS_TYPES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>

              {form.access_type === "code" && (
                <div>
                  <Label>
                    Code promo {isEdit && existingCodePresent ? "(laissez vide pour conserver)" : "*"}
                  </Label>
                  {/* Anti-autofill password manager :
                      - type=text + masquage CSS via WebkitTextSecurity (pas type=password qui declenche l'autofill des credentials admin sauvegardes pour le site)
                      - autoComplete=off + name unique + data-lpignore/1p-ignore/form-type=other pour neutraliser LastPass, 1Password, Bitwarden, Chrome PW manager */}
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="text"
                      style={showCode ? undefined : { WebkitTextSecurity: "disc", MozTextSecurity: "disc" }}
                      value={form.code_value}
                      onChange={(e) => update({ code_value: e.target.value })}
                      placeholder={isEdit && existingCodePresent ? "•••••• (inchangé)" : "Ex: ITC2026"}
                      autoComplete="off"
                      name="privilege-promo-code-not-a-password"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      data-testid="privilege-form-code-value"
                    />
                    <Button type="button" variant="outline" size="icon" onClick={() => setShowCode(!showCode)}>
                      {showCode ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  {isEdit && existingCodePresent && (
                    <p className="text-xs text-muted-foreground mt-1">Un code est déjà enregistré (chiffré). Laissez vide pour ne rien changer.</p>
                  )}
                </div>
              )}

              {form.access_type === "external_link" && (
                <div>
                  <Label>URL partenaire *</Label>
                  <Input
                    type="url"
                    value={form.external_url}
                    onChange={(e) => update({ external_url: e.target.value })}
                    placeholder="https://partenaire.com/offre-itercatus"
                    data-testid="privilege-form-external-url"
                  />
                </div>
              )}

              {form.access_type === "reservation" && (
                <div>
                  <Label>Instructions de réservation *</Label>
                  <Textarea
                    value={form.reservation_instructions}
                    onChange={(e) => update({ reservation_instructions: e.target.value })}
                    placeholder="Ex: Contactez le partenaire au 01 23 45 67 89 en mentionnant votre numéro d'adhérent."
                    rows={4}
                    data-testid="privilege-form-reservation-instructions"
                  />
                </div>
              )}

              {form.access_type === "direct_payment" && (
                <div className="space-y-2">
                  <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                    Bientôt disponible — non connecté au moteur SumUp
                  </Badge>
                  <Label>Montant (€)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={form.direct_payment_amount}
                    onChange={(e) => update({ direct_payment_amount: e.target.value })}
                    data-testid="privilege-form-direct-payment-amount"
                  />
                </div>
              )}
            </div>
          </form>
        )}

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving || loading} data-testid="privilege-form-save-draft-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Enregistrer comme brouillon
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving || loading} data-testid="privilege-form-save-activate-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Enregistrer et activer
          </Button>
        </DialogFooter>

        {/* Media selectors */}
        <MediaSelector
          open={mediaSelectorMain}
          onOpenChange={setMediaSelectorMain}
          multiple={false}
          type="photo"
          onSelect={(items) => {
            const id = Array.isArray(items) ? items[0]?.id : items?.id;
            if (id) update({ main_image_id: id });
            setMediaSelectorMain(false);
          }}
        />
        <MediaSelector
          open={mediaSelectorGallery}
          onOpenChange={setMediaSelectorGallery}
          multiple={true}
          type="photo"
          onSelect={(items) => {
            const ids = (items || []).map((m) => m.id).filter(Boolean);
            if (ids.length) update({ gallery_media_ids: Array.from(new Set([...(form.gallery_media_ids || []), ...ids])) });
            setMediaSelectorGallery(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
